import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { EmployeeApiService } from '../../employee-api.service';
import { DependantResponse } from '../../employee.model';

@Component({
  selector: 'app-dependant-confirm-page',
  imports: [...SharedMaterialUIModule],
  templateUrl: './dependant-confirm.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DependantConfirmPage {
  private readonly route = inject(ActivatedRoute);
  private readonly employeeApiService = inject(EmployeeApiService);

  protected readonly dependant = signal<DependantResponse | null>(null);
  protected readonly previewLoading = signal(true);
  protected readonly previewError = signal<string | null>(null);
  protected readonly confirmed = signal(false);
  protected readonly confirming = signal(false);
  protected readonly confirmError = signal<string | null>(null);

  private readonly employeeId = this.route.snapshot.paramMap.get('employeeId') ?? '';
  private readonly dependantId = this.route.snapshot.paramMap.get('dependantId') ?? '';
  private readonly token = this.route.snapshot.queryParamMap.get('token') ?? '';

  constructor() {
    if (!this.token) {
      this.previewError.set('This confirmation link is missing its token. Please use the link from the original email.');
      this.previewLoading.set(false);
      return;
    }

    this.employeeApiService.getDependantConfirmationPreview(this.employeeId, this.dependantId, this.token).subscribe({
      next: (d) => {
        this.dependant.set(d);
        this.previewLoading.set(false);
      },
      error: () => {
        this.previewError.set('This confirmation link is invalid or has expired. Please contact support if you believe this is a mistake.');
        this.previewLoading.set(false);
      },
    });
  }

  protected confirm(): void {
    this.confirming.set(true);
    this.confirmError.set(null);

    this.employeeApiService.confirmDependant(this.employeeId, this.dependantId, this.token).subscribe({
      next: (d) => {
        this.dependant.set(d);
        this.confirmed.set(true);
        this.confirming.set(false);
      },
      error: () => {
        this.confirmError.set('Confirmation failed. Please try again or contact support.');
        this.confirming.set(false);
      },
    });
  }
}
